import * as Sentry from '@sentry/react';
import { toLower } from 'lodash-es';
import React, { ReactElement, useEffect } from 'react';
import { version } from './values';
//performance related
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom';

const envArray = [
  ['production', 'com'],
  ['staging', 'net'],
  ['development', 'dev']
];

const sentryDsnMap = {
  'access.threatadvisor.com': {
    environment: 'production',
    dsn: 'https://e524c4623eaf0895ccbe51a4fe4d12ce@sentry.threatadvisor.com/2'
  },
  'trial.threatadvisor.com': {
    environment: 'trial',
    dsn: 'https://e524c4623eaf0895ccbe51a4fe4d12ce@sentry.threatadvisor.com/2'
  },
  'taweb.threatadvisor.net': {
    environment: 'taweb',
    dsn: 'https://e524c4623eaf0895ccbe51a4fe4d12ce@sentry.threatadvisor.com/2'
  }
};

envArray.forEach(e => {
  sentryDsnMap[`casemanagement.threatadvisor.${e[1]}`] = {
    environment: e[0],
    dsn: 'https://a1183d92d7cb2e0f199af16b251abb78@sentry.threatadvisor.com/18'
  };

  sentryDsnMap[`security-testing.threatadvisor.${e[1]}`] = {
    environment: e[0],
    dsn: `https://7c10c47c13472dab1775335c4556c012@sentry.threatadvisor.com/19`
  };

  sentryDsnMap[`mdr.threatadvisor.${e[1]}`] = {
    environment: e[0],
    dsn: `https://9cdd9963b4ed7e6a9bc0e590154748bd@sentry.threatadvisor.com/23`
  };
  sentryDsnMap[`vciso.threatadvisor.${e[1]}`] = {
    environment: e[0],
    dsn: `https://9dfadb8e889737793b807e73980d0e13@sentry.threatadvisor.com/24`
  };
  sentryDsnMap[`essentials.threatadvisor.${e[1]}`] = {
    environment: e[0],
    dsn: `https://6de337ea972bb7fed7500503216f898b@sentry.threatadvisor.com/25`
  };

  sentryDsnMap[`workbench.threatadvisor.${e[1]}`] = {
    environment: e[0],
    dsn: `https://5b2964e134db6e33b80259475ae226a6@sentry.threatadvisor.com/20`
  };

  sentryDsnMap[`provisioning.threatadvisor.${e[1]}`] = {
    environment: e[0],
    dsn: `https://c22d7907ddaeb1b0cd9ce84161a60724@sentry.threatadvisor.com/21`
  };

  sentryDsnMap[`missioncontrol.threatadvisor.${e[1]}`] = {
    environment: e[0],
    dsn: `https://fe5c979339fe4de025280ffe727dab32@sentry.threatadvisor.com/22`
  };
});

// Define a global variable to track Sentry initialization
declare global {
  interface Window {
    __sentryInitialized?: boolean;
  }
}

const { environment, dsn } = sentryDsnMap[window.location.host] ?? {
  environment: 'Local',
  dsn: 'https://04f00523fa1743afa8787215642ff62a@sentry.threatadvisor.com/8'
};

if (environment !== 'Local' && !window.__sentryInitialized && !Sentry.getClient()) {
  const windowerrorExclusions: { name?: string; message: string }[] = [
    { message: 'ResizeObserver loop limit exceeded' }
  ];
  Sentry.init({
    dsn: dsn,
    environment: toLower(environment),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      })
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    debug: environment === 'development',
    release: version,
    beforeSend(event, hint) {
      const originalError = hint?.originalException;

      let errorName: string | undefined;
      let errorMessage: string | undefined;

      if (originalError instanceof Error) {
        errorName = originalError.name;
        errorMessage = originalError.message;
      } else if (typeof originalError === 'string') {
        errorMessage = originalError;
      }

      if (
        originalError &&
        windowerrorExclusions.some(e => {
          const matchesName = !e.name || e.name === errorName;
          const matchesMessage = !e.message || e.message === errorMessage;
          return matchesName && matchesMessage;
        })
      ) {
        // Drop this event completely
        return null;
      }

      if (originalError) {
        console.error(originalError);
      }

      return event;
    },
    beforeBreadcrumb(event, hint) {
      const target = (hint as any)?.event?.target as HTMLElement | undefined;

      if (event.category === 'ui.click' && target && ['BUTTON', 'A'].includes(target.tagName)) {
        const label = target.innerText || target.textContent || target.innerHTML || '';
        event.message = (event.message || 'ui.click') + ` "${label.trim()}"`;
      }

      return event;
    }
  });
  // Mark Sentry as initialized
  window.__sentryInitialized = true;
}

export function RootErrorBoundary({ children }: { children: ReactElement }): ReactElement {
  if (environment === 'Local') return children;
  return (
    <Sentry.ErrorBoundary
      showDialog
      fallback={<>Error has been recorded. Please refresh your page.</>}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export function sendToSentry(error: Error): void {
  if (environment !== 'Local') Sentry.captureException(error);
}
